import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
	type AuditQuery,
	adminSecurityApi,
	approvalsApi,
	auditApi,
	type CreateTeamPayload,
	type SubmitApprovalPayload,
	teamsApi,
} from "@/lib/admin/admin-l4-api";

// ─── Teams ────────────────────────────────────────

export const useTeamsList = () =>
	useQuery({ queryKey: ["admin", "teams"], queryFn: teamsApi.list });

export const useTeam = (id: string | undefined) =>
	useQuery({
		queryKey: ["admin", "teams", id],
		queryFn: () => teamsApi.get(id!),
		enabled: !!id,
	});

export const useCreateTeam = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (p: CreateTeamPayload) => teamsApi.create(p),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "teams"] }),
	});
};

export const useUpdateTeam = (id: string) => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (p: Partial<CreateTeamPayload>) => teamsApi.update(id, p),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "teams"] }),
	});
};

export const useDeleteTeam = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => teamsApi.remove(id),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "teams"] }),
	});
};

// ─── Audit ───────────────────────────────────────

export const useAuditList = (q: AuditQuery) =>
	useQuery({
		queryKey: ["admin", "audit", q],
		queryFn: () => auditApi.list(q),
		staleTime: 10_000,
	});

// ─── Security ────────────────────────────────────

export const useGlobalSessions = () =>
	useQuery({
		queryKey: ["admin", "security", "sessions"],
		queryFn: adminSecurityApi.sessions,
		refetchInterval: 15_000,
	});

export const useRevokeGlobalSession = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => adminSecurityApi.revokeSession(id),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "security"] }),
	});
};

export const useFailedAttempts = () =>
	useQuery({
		queryKey: ["admin", "security", "failed-attempts"],
		queryFn: adminSecurityApi.failedAttempts,
	});

// ─── Approvals ───────────────────────────────────

export const useApprovalsList = (status?: string) =>
	useQuery({
		queryKey: ["admin", "approvals", status],
		queryFn: () => approvalsApi.list(status),
		refetchInterval: 30_000,
	});

export const useSubmitApproval = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (p: SubmitApprovalPayload) => approvalsApi.submit(p),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "approvals"] }),
	});
};

export const useApproveApproval = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, note }: { id: string; note?: string }) =>
			approvalsApi.approve(id, note),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "approvals"] }),
	});
};

export const useRejectApproval = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, note }: { id: string; note: string }) =>
			approvalsApi.reject(id, note),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "approvals"] }),
	});
};
